import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import api, { errMsg } from "../api";
import { MessageSquare, Send, Plus, Users, Search, Paperclip, Loader2, X, ChevronLeft } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

function isOnline(u) {
    if (!u?.last_seen) return false;
    return (Date.now() - new Date(u.last_seen).getTime()) / 1000 < 90;
}

export default function ChatPage() {
    const { lang, user } = useAuth();
    const [chats, setChats] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [newChatOpen, setNewChatOpen] = useState(false);
    const [search, setSearch] = useState("");
    const wsRef = useRef(null);
    const fileRef = useRef(null);
    const scrollRef = useRef(null);
    const dingRef = useRef(null);

    // Short ding sound (50ms)
    const DING_URI = "data:audio/wav;base64,UklGRiQEAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAEAACAgIB+gIB+f4B/foCAfn5/gH9+f4F/f3+Af4CAgIB/gH+AgIB/gIB/gIB/gIB/gIB/gIB/gH9/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/";

    const loadChats = useCallback(async () => {
        try {
            const { data } = await api.get("/chats");
            setChats(data);
        } catch (e) { console.error(e); }
    }, []);

    const loadEmployees = useCallback(async () => {
        try {
            const { data } = await api.get("/employees");
            setEmployees(data);
        } catch {}
    }, []);

    useEffect(() => { loadChats(); loadEmployees(); }, [loadChats, loadEmployees]);

    // Auto-select chat from ?chat=<id> deep link (from /employees etc.)
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const chatId = searchParams.get("chat");
        if (chatId && chats.length > 0 && (!activeChat || activeChat.id !== chatId)) {
            const found = chats.find((c) => c.id === chatId);
            if (found) setActiveChat(found);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chats, searchParams]);

    const loadMessages = useCallback(async (chatId) => {
        try {
            const { data } = await api.get(`/chats/${chatId}/messages?limit=100`);
            setMessages(data);
            await api.post(`/chats/${chatId}/read`);
            loadChats();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    }, [loadChats]);

    // Open WebSocket on active chat
    useEffect(() => {
        if (!activeChat) return;
        loadMessages(activeChat.id);
        const token = localStorage.getItem("taskflow_token");
        const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
        const ws = new WebSocket(`${base}/api/ws/chat/${activeChat.id}?token=${encodeURIComponent(token || "")}`);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
            try {
                const d = JSON.parse(ev.data);
                if (d.type === "message" && d.message?.chat_id === activeChat.id) {
                    if (d.message.user_id !== user?.id) {
                        try { dingRef.current?.play().catch(() => {}); } catch {}
                    }
                    setMessages((prev) => prev.find((m) => m.id === d.message.id) ? prev : [...prev, d.message]);
                    api.post(`/chats/${activeChat.id}/read`).catch(() => {});
                }
            } catch {}
        };
        ws.onclose = () => { wsRef.current = null; };
        return () => { try { ws.close(); } catch {} };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeChat?.id]);

    // Auto-scroll on new message
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    const send = async () => {
        if (!text.trim() || !activeChat) return;
        try {
            const { data: msg } = await api.post(`/chats/${activeChat.id}/messages`, { text });
            // Optimistic append (WS also broadcasts but may be unavailable)
            setMessages((prev) => prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]);
            setText("");
            loadChats();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const uploadFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f || !activeChat) return;
        const form = new FormData();
        form.append("file", f);
        try {
            const { data } = await api.post(`/chats/${activeChat.id}/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
            const { data: msg } = await api.post(`/chats/${activeChat.id}/messages`, { text: "", attachments: [data] });
            setMessages((prev) => prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]);
            loadChats();
            if (fileRef.current) fileRef.current.value = "";
        } catch (err) { toast.error(errMsg(err, "Upload error")); }
    };

    const filteredChats = chats.filter((c) => !search || (c.display_name || "").toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="flex h-[calc(100vh-64px)]" data-testid="chat-page">
            <audio ref={dingRef} src={DING_URI} preload="auto" />
            {/* Sidebar */}
            <aside className={`${activeChat ? "hidden md:flex" : "flex"} w-full md:w-80 border-r border-neutral-200 bg-white flex-col`}>
                <div className="p-3 border-b border-neutral-200">
                    <div className="flex items-center justify-between mb-2">
                        <h1 className="font-display text-lg font-bold flex items-center gap-1.5">
                            <MessageSquare className="w-4 h-4" /> {t(lang, "chat")}
                        </h1>
                        <Button size="sm" onClick={() => setNewChatOpen(true)} data-testid="new-chat-btn" className="h-8 px-2 bg-neutral-900 hover:bg-neutral-800">
                            <Plus className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t(lang, "search")} className="h-8 pl-8 text-xs" data-testid="chat-search" />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {filteredChats.length === 0 ? (
                        <p className="p-6 text-center text-xs text-neutral-500">{t(lang, "noChats")}</p>
                    ) : filteredChats.map((c) => {
                        const active = activeChat?.id === c.id;
                        const online = !c.is_group && isOnline(c.other_user);
                        return (
                            <button
                                key={c.id}
                                data-testid={`chat-item-${c.id}`}
                                onClick={() => setActiveChat(c)}
                                className={`w-full flex items-start gap-2.5 p-3 hover:bg-neutral-50 border-b border-neutral-100 text-left ${active ? "bg-blue-50" : ""}`}
                            >
                                <div className="relative shrink-0">
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-xs ${c.is_group ? "bg-blue-600" : "bg-neutral-900"}`}>
                                        {c.is_group ? <Users className="w-4 h-4" /> : (c.display_name || "?").slice(0, 2).toUpperCase()}
                                    </div>
                                    {!c.is_group && (
                                        <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${online ? "bg-emerald-500" : "bg-neutral-300"}`} />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                        <p className="font-semibold text-sm truncate">{c.display_name}</p>
                                        {c.last_message && (
                                            <span className="text-[10px] text-neutral-400 shrink-0 ml-1">
                                                {new Date(c.last_message.created_at).toLocaleTimeString(LOCALES[lang], { hour: "2-digit", minute: "2-digit" })}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-neutral-500 truncate">
                                        {c.last_message ? `${c.last_message.user_name}: ${c.last_message.text}` : <span className="italic">—</span>}
                                    </p>
                                </div>
                                {c.unread_count > 0 && (
                                    <span data-testid={`chat-unread-${c.id}`} className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{c.unread_count}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 flex flex-col bg-neutral-50">
                {!activeChat ? (
                    <div className="flex-1 flex items-center justify-center text-neutral-400">
                        <div className="text-center">
                            <MessageSquare className="w-16 h-16 mx-auto opacity-30 mb-3" />
                            <p>{t(lang, "selectChat")}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <header className="px-3 py-2 sm:px-4 sm:py-3 border-b border-neutral-200 bg-white flex items-center gap-2 sm:gap-3" data-testid="chat-header">
                            <button
                                onClick={() => setActiveChat(null)}
                                className="md:hidden p-1 -ml-1 rounded hover:bg-neutral-100"
                                data-testid="chat-back-btn"
                                aria-label="Zurück"
                            >
                                <ChevronLeft className="w-5 h-5 text-neutral-700" />
                            </button>
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-xs ${activeChat.is_group ? "bg-blue-600" : "bg-neutral-900"}`}>
                                {activeChat.is_group ? <Users className="w-4 h-4" /> : (activeChat.display_name || "?").slice(0, 2).toUpperCase()}
                            </div>
                            <div className="flex-1">
                                <p className="font-semibold">{activeChat.display_name}</p>
                                <p className="text-xs text-neutral-500">
                                    {activeChat.is_group ? `${(activeChat.member_ids || []).length} ${t(lang, "members")}` : (isOnline(activeChat.other_user) ? <span className="text-emerald-600">● {t(lang, "online")}</span> : t(lang, "offline"))}
                                </p>
                            </div>
                        </header>
                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
                            {messages.length === 0 ? (
                                <p className="text-center text-xs text-neutral-400 py-8">{t(lang, "noMessages")}</p>
                            ) : messages.map((m) => {
                                const mine = m.user_id === user?.id;
                                return (
                                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                                        <div className={`max-w-[70%] ${mine ? "bg-blue-600 text-white" : "bg-white border border-neutral-200"} rounded-2xl px-3.5 py-2`}>
                                            {!mine && activeChat.is_group && <p className="text-[10px] font-semibold text-blue-600 mb-0.5">{m.user_name}</p>}
                                            {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
                                            {(m.attachments || []).map((a) => (
                                                <a
                                                    key={a.id}
                                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.id}/download?auth=${encodeURIComponent(localStorage.getItem("taskflow_token") || "")}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={`flex items-center gap-1.5 text-xs ${mine ? "text-blue-100" : "text-blue-700"} underline mt-1`}
                                                >
                                                    <Paperclip className="w-3 h-3" />{a.name}
                                                </a>
                                            ))}
                                            <p className={`text-[10px] mt-0.5 ${mine ? "text-blue-200" : "text-neutral-400"}`}>
                                                {new Date(m.created_at).toLocaleTimeString(LOCALES[lang], { hour: "2-digit", minute: "2-digit" })}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <footer className="p-3 bg-white border-t border-neutral-200 flex items-center gap-2">
                            <button onClick={() => fileRef.current?.click()} data-testid="chat-attach-btn" className="p-2 text-neutral-500 hover:text-neutral-900">
                                <Paperclip className="w-5 h-5" />
                            </button>
                            <input ref={fileRef} type="file" onChange={uploadFile} className="hidden" data-testid="chat-file-input" />
                            <Input
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                                placeholder={t(lang, "typeMessage")}
                                data-testid="chat-input"
                                className="flex-1"
                            />
                            <Button onClick={send} disabled={!text.trim()} data-testid="chat-send-btn" className="bg-blue-600 hover:bg-blue-700">
                                <Send className="w-4 h-4" />
                            </Button>
                        </footer>
                    </>
                )}
            </main>

            <NewChatDialog
                open={newChatOpen}
                onClose={() => setNewChatOpen(false)}
                onCreated={(c) => { loadChats(); setActiveChat(c); }}
                employees={employees.filter((e) => e.id !== user?.id)}
                lang={lang}
            />
        </div>
    );
}

function NewChatDialog({ open, onClose, onCreated, employees, lang }) {
    const [members, setMembers] = useState([]);
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (open) { setMembers([]); setName(""); } }, [open]);

    const toggle = (id) => setMembers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    const isGroup = members.length > 1;

    const submit = async () => {
        if (members.length === 0) return;
        if (isGroup && !name.trim()) {
            toast.error(t(lang, "groupName"));
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/chats", { member_ids: members, name: isGroup ? name : null, is_group: isGroup });
            onCreated(data);
            onClose();
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="new-chat-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{isGroup ? t(lang, "newGroup") : t(lang, "newChat")}</DialogTitle>
                    <DialogDescription className="text-xs">{lang === "de" ? "Wähle 1 Mitarbeiter für Direktnachricht, 2+ für Gruppe." : "1 = direct, 2+ = group"}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {isGroup && (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "groupName")}</Label>
                            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="chat-group-name" />
                        </div>
                    )}
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "selectEmployee")}</Label>
                        <div className="mt-1.5 max-h-[300px] overflow-y-auto border border-neutral-200 rounded-md divide-y divide-neutral-100">
                            {employees.map((e) => {
                                const sel = members.includes(e.id);
                                return (
                                    <button
                                        key={e.id}
                                        type="button"
                                        onClick={() => toggle(e.id)}
                                        data-testid={`pick-${e.id}`}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-left ${sel ? "bg-blue-50" : "hover:bg-neutral-50"}`}
                                    >
                                        <input type="checkbox" checked={sel} readOnly className="w-3.5 h-3.5" />
                                        <div className="w-7 h-7 rounded-full bg-neutral-900 text-white text-[10px] font-semibold flex items-center justify-center">{e.name?.slice(0, 2)?.toUpperCase()}</div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{e.name}</p>
                                            {e.job_title && <p className="text-[10px] text-neutral-500 truncate">{e.job_title}</p>}
                                        </div>
                                        {isOnline(e) && <span className="w-2 h-2 bg-emerald-500 rounded-full" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy || members.length === 0 || (isGroup && !name.trim())} data-testid="chat-create-btn" className="bg-neutral-900 hover:bg-neutral-800">
                        {t(lang, "create")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
